import React,{useState,useRef} from 'react';
import {useAppContext} from '../context/AppContext';
import {getPricePerPixel} from '../utils/pricing';
import {createCheckoutSession} from '../utils/stripe';
import {getStoredReferralCode} from '../utils/affiliate';
import * as FiIcons from 'react-icons/fi';
const {FiZap,FiX,FiLock,FiGift,FiShoppingCart,FiImage,FiUpload,FiLink,FiExternalLink,FiAlertCircle}=FiIcons;
const PENDING_TILE_KEY='pending_tile_purchase';

function ImageUploadZone({onFileSelect,preview}) {
  const inputRef=useRef(null);
  const [dragging,setDragging]=useState(false);
  const handleDrop=(e)=> {
    e.preventDefault();setDragging(false);
    const file=e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) onFileSelect(file);
  };
  return (
    <div>
      <div
        onClick={()=> inputRef.current?.click()}
        onDragOver={(e)=> {e.preventDefault();setDragging(true);}}
        onDragLeave={()=> setDragging(false)}
        onDrop={handleDrop}
        className="w-full rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-all"
        style={{height:'110px',background:dragging?'rgba(255,0,200,0.1)':'rgba(255,255,255,0.03)',border:`2px dashed ${dragging?'#ff00cc':'rgba(255,0,200,0.25)'}`}}
      >
        {preview?(
          <img src={preview} alt="preview" className="h-full w-full object-contain rounded-xl p-1" />
        ):(
          <>
            <FiUpload className="text-2xl" style={{color:'#ff00cc'}} />
            <span className="font-bold text-gray-400" style={{fontSize:'14px'}}>Drop image here or <span style={{color:'#ff00cc'}}>browse</span></span>
            <span className="text-gray-600" style={{fontSize:'12px'}}>PNG,JPG,GIF,WebP</span>
          </>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e)=> {const file=e.target.files?.[0];if(file)onFileSelect(file);}} />
    </div>
  );
}

export default function PurchaseModal() {
  const {selection,setSelection,isPurchaseModalOpen,setIsPurchaseModalOpen,pixelsSold,addTile,user}=useAppContext();
  const [formData,setFormData]=useState({title:'',link:'',imageUrl:'',blockText:'',blockTextColor:'#ffffff',blockBgColor:'#ff00cc',hasGif:false,hasGlow:false,isDofollow:false});
  const [imageMode,setImageMode]=useState('upload');
  const [uploadedFile,setUploadedFile]=useState(null);
  const [uploadPreview,setUploadPreview]=useState('');
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');

  if (!isPurchaseModalOpen || !selection) return null;

  const totalPixels=selection.w * selection.h;
  const pricePerPx=getPricePerPixel(pixelsSold);
  const basePrice=totalPixels * pricePerPx;
  const addonsPrice=(formData.hasGif?29:0)+(formData.hasGlow?15:0)+(formData.isDofollow?19:0);
  const finalPrice=basePrice+addonsPrice;
  const finalPriceCents=Math.round(finalPrice*100);

  const handleFileSelect=(file)=> {
    setUploadedFile(file);
    const reader=new FileReader();
    reader.onload=(e)=> setUploadPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const buildTilePayload=()=> {
    const referralCode=getStoredReferralCode() || null;
    const base={
      x:selection.x,y:selection.y,w:selection.w,h:selection.h,
      title:formData.title||'Anonymous',
      link:formData.link||'https://example.com',
      hasGlow:formData.hasGlow,hasGif:formData.hasGif,isDofollow:formData.isDofollow,
      ownerEmail:user?.email||'',
      referralCode,
      source: referralCode ? 'referral' : 'direct',
    };
    if (imageMode==='upload' && uploadPreview) return {...base,image:uploadPreview};
    if (imageMode==='url' && formData.imageUrl) return {...base,image:formData.imageUrl};
    if (imageMode==='text' && formData.blockText) return {...base,solidColor:formData.blockBgColor,blockText:formData.blockText,blockTextColor:formData.blockTextColor};
    return {...base,solidColor:'#1a0030'};
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();setError('');setLoading(true);
    try {
      const tile=buildTilePayload();
      localStorage.setItem(PENDING_TILE_KEY,JSON.stringify(tile));
      const origin=window.location.origin+window.location.pathname;
      const successUrl=`${origin}?payment=success`;
      const cancelUrl=`${origin}?payment=cancelled`;
      const {url}=await createCheckoutSession({tile,priceInCents:finalPriceCents,successUrl,cancelUrl});
      window.location.href=url;
    } catch(err) {
      console.error('Checkout error:',err);
      setError(err.message||'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  const close=()=> {setIsPurchaseModalOpen(false);setSelection(null);setError('');};
  const inputStyle={background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,0,200,0.2)',fontSize:'15px'};
  const labelStyle={fontSize:'12px'};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
      <div className="relative w-full max-w-md animate-slide-up">
        <div className="absolute -inset-[1px] rounded-2xl opacity-70" style={{background:'linear-gradient(135deg,#ff00cc,#cc00ff,#ff00cc)'}} />
        <div className="relative rounded-2xl overflow-hidden flex flex-col max-h-[90vh]" style={{background:'#0a0014'}}>
          <div className="p-4 flex items-center justify-between border-b border-pink-500/20 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{background:'linear-gradient(135deg,#ff00cc,#cc00ff)'}}>
                <FiShoppingCart className="text-white text-base" />
              </div>
              <div>
                <h2 className="font-black text-white uppercase tracking-wider" style={{fontSize:'16px'}}>Secure Your Pixels</h2>
                <div className="text-gray-500" style={{fontSize:'12px'}}>Permanent • Immutable • Yours Forever</div>
              </div>
            </div>
            <button onClick={close} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all flex items-center justify-center"><FiX /></button>
          </div>

          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 p-3 rounded-xl border border-pink-500/20" style={{background:'rgba(255,0,200,0.05)'}}>
              <div>
                <div className="text-gray-500 uppercase tracking-widest mb-0.5" style={{fontSize:'11px'}}>Selected Area</div>
                <div className="font-mono font-black text-white" style={{fontSize:'16px'}}>{selection.w} × {selection.h}px</div>
                <div className="text-gray-500 mt-0.5" style={{fontSize:'12px'}}>{totalPixels.toLocaleString()} total pixels</div>
              </div>
              <div className="text-right">
                <div className="text-gray-500 uppercase tracking-widest mb-0.5" style={{fontSize:'11px'}}>Base Price</div>
                <div className="font-black text-white" style={{fontSize:'28px'}}>${basePrice.toFixed(2)}</div>
                <div style={{color:'#ff00cc',fontSize:'13px'}}>${pricePerPx.toFixed(2)}/px</div>
              </div>
            </div>

            <div className="flex items-center gap-2 p-2 rounded-lg border border-yellow-500/20" style={{background:'rgba(255,200,0,0.05)'}}>
              <FiZap className="text-yellow-400 shrink-0" />
              <span className="text-yellow-300 font-bold" style={{fontSize:'13px'}}>⚡ {(1000000-pixelsSold).toLocaleString()} pixels left at this price!</span>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block font-bold text-gray-400 uppercase tracking-wider mb-1" style={labelStyle}>Brand / Title *</label>
                <input required type="text" value={formData.title} onChange={e=> setFormData({...formData,title:e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-white focus:outline-none transition-all" style={inputStyle} onFocus={e=> e.target.style.borderColor='rgba(255,0,200,0.6)'} onBlur={e=> e.target.style.borderColor='rgba(255,0,200,0.2)'} placeholder="Acme Corp" />
              </div>
              <div>
                <label className="block font-bold text-gray-400 uppercase tracking-wider mb-1" style={labelStyle}>Target URL *</label>
                <input required type="url" value={formData.link} onChange={e=> setFormData({...formData,link:e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-white focus:outline-none transition-all" style={inputStyle} onFocus={e=> e.target.style.borderColor='rgba(255,0,200,0.6)'} onBlur={e=> e.target.style.borderColor='rgba(255,0,200,0.2)'} placeholder="https://..." />
              </div>
            </div>

            <div>
              <div className="font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1" style={{fontSize:'12px'}}>
                <FiImage className="text-pink-500" /> What to Display on Grid
              </div>
              <div className="flex rounded-xl overflow-hidden border border-gray-800 mb-3">
                {[{id:'upload',label:'⬆ Upload'},{id:'url',label:'🔗 URL'},{id:'text',label:'🔤 Text'}].map(opt=> (
                  <button key={opt.id} type="button" onClick={()=> setImageMode(opt.id)} className="flex-1 py-2 font-bold transition-all" style={{fontSize:'13px',...(imageMode===opt.id?{background:'rgba(255,0,200,0.15)',color:'#ff00cc',borderRight:'1px solid rgba(255,0,200,0.2)'}:{color:'#6b7280'})}}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {imageMode==='upload' && (
                <div>
                  <ImageUploadZone onFileSelect={handleFileSelect} preview={uploadPreview} />
                  {uploadPreview && <button type="button" onClick={()=> {setUploadedFile(null);setUploadPreview('');}} className="mt-1.5 text-red-400 hover:text-red-300 transition-colors" style={{fontSize:'13px'}}>✕ Remove image</button>}
                </div>
              )}
              {imageMode==='url' && (
                <div>
                  <input type="url" value={formData.imageUrl} onChange={e=> setFormData({...formData,imageUrl:e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-white focus:outline-none transition-all" style={inputStyle} onFocus={e=> e.target.style.borderColor='rgba(255,0,200,0.6)'} onBlur={e=> e.target.style.borderColor='rgba(255,0,200,0.2)'} placeholder="https://yoursite.com/logo.png" />
                  {formData.imageUrl && <div className="mt-2 w-16 h-16 rounded-lg overflow-hidden border border-pink-500/30"><img src={formData.imageUrl} alt="preview" className="w-full h-full object-cover" onError={e=> {e.target.style.display='none';}} /></div>}
                </div>
              )}
              {imageMode==='text' && (
                <div className="space-y-3">
                  <div>
                    <label className="block font-bold text-gray-400 uppercase tracking-wider mb-1" style={labelStyle}>Block Text</label>
                    <input type="text" value={formData.blockText} onChange={e=> setFormData({...formData,blockText:e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-white focus:outline-none transition-all" style={inputStyle} onFocus={e=> e.target.style.borderColor='rgba(255,0,200,0.6)'} onBlur={e=> e.target.style.borderColor='rgba(255,0,200,0.2)'} placeholder="BUY NOW · ACME.COM" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-gray-400 uppercase tracking-wider mb-1" style={labelStyle}>Text Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={formData.blockTextColor} onChange={e=> setFormData({...formData,blockTextColor:e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
                        <span className="text-gray-400 font-mono" style={{fontSize:'13px'}}>{formData.blockTextColor}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block font-bold text-gray-400 uppercase tracking-wider mb-1" style={labelStyle}>BG Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={formData.blockBgColor} onChange={e=> setFormData({...formData,blockBgColor:e.target.value})} className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
                        <span className="text-gray-400 font-mono" style={{fontSize:'13px'}}>{formData.blockBgColor}</span>
                      </div>
                    </div>
                  </div>
                  {formData.blockText && (
                    <div className="rounded-lg overflow-hidden h-14 flex items-center justify-center border border-pink-500/20" style={{background:formData.blockBgColor}}>
                      <span className="font-black text-center px-2 leading-tight" style={{color:formData.blockTextColor,fontSize:'18px'}}>{formData.blockText}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1" style={{fontSize:'12px'}}>
                <FiGift className="text-pink-500" /> Premium Add-ons
              </div>
              <div className="space-y-2">
                {[{key:'hasGif',label:'Animated GIF Support',price:29,icon:'🎬'},{key:'hasGlow',label:'Neon Glow Border',price:15,icon:'✨'},{key:'isDofollow',label:'Dofollow SEO Backlink',price:19,icon:'🔗'}].map(addon=> (
                  <label key={addon.key} className="flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all" style={{background:formData[addon.key]?'rgba(255,0,200,0.08)':'rgba(255,255,255,0.03)',border:`1px solid ${formData[addon.key]?'rgba(255,0,200,0.3)':'rgba(255,255,255,0.06)'}`}}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={formData[addon.key]} onChange={e=> setFormData({...formData,[addon.key]:e.target.checked})} className="w-4 h-4 rounded accent-pink-500" />
                      <span className="text-gray-300" style={{fontSize:'14px'}}>{addon.icon} {addon.label}</span>
                    </div>
                    <span className="font-black" style={{color:'#ff00cc',fontSize:'14px'}}>+${addon.price}</span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl text-red-400" style={{background:'rgba(255,50,50,0.08)',border:'1px solid rgba(255,50,50,0.25)',fontSize:'13px'}}>
                <FiAlertCircle className="shrink-0 mt-0.5" /><span>{error}</span>
              </div>
            )}

            <div className="pt-2 border-t border-pink-500/10">
              {addonsPrice>0 && <div className="flex justify-between text-gray-500 mb-1" style={{fontSize:'14px'}}><span>Add-ons</span><span>+${addonsPrice}</span></div>}
              <div className="flex justify-between font-black text-white mb-3" style={{fontSize:'17px'}}>
                <span>Total</span><span style={{color:'#ff00cc'}}>${finalPrice.toFixed(2)}</span>
              </div>
              <button type="submit" disabled={loading} className="w-full py-4 rounded-xl font-black uppercase tracking-widest text-white transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70" style={{fontSize:'18px',background:loading?'rgba(255,0,200,0.5)':'linear-gradient(135deg,#ff00cc,#cc00ff)',boxShadow:'0 0 20px rgba(255,0,200,0.4)',fontFamily:'Impact,sans-serif',letterSpacing:'2px'}}>
                {loading?(<><div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Redirecting to Stripe...</>):(<><FiLock /> Pay with Stripe — ${finalPrice.toFixed(2)} <FiExternalLink /></>)}
              </button>
              <p className="text-center text-gray-600 mt-2" style={{fontSize:'12px'}}>🔒 Secured by Stripe · You'll be redirected to complete payment</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}